import React, { useState, useCallback, useEffect } from 'react';
import {
  RefreshCw,
  Database,
  Merge,
  Play,
  Square,
  Calendar,
  ArrowRight,
  ArrowDown
} from 'lucide-react';
import RawDataAnalysis from './RawDataAnalysis';
import { getScraperUrl, SCRAPER_CONFIG } from '../../../constants/scraper';

const ScraperManagementDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [regenerationLoading, setRegenerationLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [regenerateProcessedLoading, setRegenerateProcessedLoading] =
    useState(false);
  const [cyclicLoading, setCyclicLoading] = useState(false);
  const [error, setError] = useState(null);
  const [regenerationStatus, setRegenerationStatus] = useState(null);
  const [mergeStatus, setMergeStatus] = useState(null);
  const [regenerateProcessedStatus, setRegenerateProcessedStatus] =
    useState(null);
  const [cyclicStatus, setCyclicStatus] = useState(null);
  // Dynamically determine current year (2-digit format)
  const getCurrentYearPrefix = () => {
    const now = new Date();
    return now.getFullYear().toString().slice(-2);
  };

  const [selectedYear, setSelectedYear] = useState(getCurrentYearPrefix());

  // Simplified refresh function
  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Refresh is handled by RawDataAnalysis component
    setLoading(false);
  }, []);

  const pollRegenerationStatus = useCallback(async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.REGENERATE_STATUS)
        );
        const data = await response.json();

        if (data.success) {
          setRegenerationStatus(data.status);

          // If regeneration is complete, stop polling and refresh data
          if (!data.status.running) {
            clearInterval(pollInterval);
            setRegenerationLoading(false);
            if (data.status.error) {
              setError(`Regeneration failed: ${data.status.error}`);
            } else {
              // Refresh the case summary data
              refreshAll();
            }
          }
        }
      } catch (err) {
        console.error('Error polling regeneration status:', err);
        clearInterval(pollInterval);
        setRegenerationLoading(false);
      }
    }, 2000); // Poll every 2 seconds

    // Cleanup interval after 10 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval);
      setRegenerationLoading(false);
    }, 600000);
  }, [refreshAll]);

  // Trigger merge process
  const triggerMerge = useCallback(
    async (createBackup = true) => {
      // Show confirmation dialog for no-backup merge
      if (!createBackup) {
        const confirmed = window.confirm(
          '⚠️ WARNING: You are about to run a merge WITHOUT creating a backup.\n\n' +
            'This means you will not be able to easily rollback if something goes wrong.\n\n' +
            'Are you sure you want to continue?'
        );
        if (!confirmed) {
          return;
        }
      }

      setMergeLoading(true);
      setError(null);
      const backupText = createBackup ? 'with backup' : 'without backup';
      setMergeStatus({
        running: true,
        message: `Starting merge process ${backupText}...`
      });

      try {
        const response = await fetch(
          'https://docket-scraper-merge-7250e581e175.herokuapp.com/merge/trigger',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ create_backup: createBackup })
          }
        );
        const data = await response.json();

        if (data.success) {
          setMergeStatus({
            running: false,
            success: true,
            message: `Merge process completed successfully ${backupText}!`
          });
          // Refresh data after merge
          setTimeout(() => {
            refreshAll();
            setMergeStatus(null);
          }, 3000);
        } else {
          setMergeStatus({
            running: false,
            success: false,
            message: `Merge failed: ${data.error || 'Unknown error'}`
          });
          setTimeout(() => setMergeStatus(null), 5000);
        }
      } catch (err) {
        setMergeStatus({
          running: false,
          success: false,
          message: 'Failed to trigger merge process'
        });
        console.error('Error triggering merge:', err);
        setTimeout(() => setMergeStatus(null), 5000);
      } finally {
        setMergeLoading(false);
      }
    },
    [refreshAll]
  );

  // Trigger regenerate processed data (consolidates user edits)
  const triggerRegenerateProcessedData = useCallback(async () => {
    const confirmed = window.confirm(
      '🔄 Regenerate Processed Data\n\n' +
        'This will regenerate ALL processed data files from raw data and consolidate user edits.\n\n' +
        'This process may take several minutes. Continue?'
    );
    if (!confirmed) {
      return;
    }

    setRegenerateProcessedLoading(true);
    setError(null);
    setRegenerateProcessedStatus({
      running: true,
      message: 'Starting processed data regeneration...'
    });

    try {
      const response = await fetch(
        'https://docket-scraper-merge-7250e581e175.herokuapp.com/merge/regenerate-processed-data',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const data = await response.json();

      if (data.success) {
        setRegenerateProcessedStatus({
          running: false,
          success: true,
          message:
            'Processed data regeneration completed successfully! User edits have been consolidated.'
        });
        // Refresh data after regeneration
        setTimeout(() => {
          refreshAll();
          setRegenerateProcessedStatus(null);
        }, 3000);
      } else {
        setRegenerateProcessedStatus({
          running: false,
          success: false,
          message: `Regeneration failed: ${data.error || 'Unknown error'}`
        });
        setTimeout(() => setRegenerateProcessedStatus(null), 5000);
      }
    } catch (err) {
      setRegenerateProcessedStatus({
        running: false,
        success: false,
        message: 'Failed to trigger processed data regeneration'
      });
      console.error('Error triggering processed data regeneration:', err);
      setTimeout(() => setRegenerateProcessedStatus(null), 5000);
    } finally {
      setRegenerateProcessedLoading(false);
    }
  }, [refreshAll]);

  // Regenerate case summary
  const regenerateSummary = useCallback(async () => {
    setRegenerationLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.REGENERATE_SUMMARY),
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        // Start polling for regeneration status
        pollRegenerationStatus();
      } else {
        setError(
          `Failed to regenerate summary: ${data.error || 'Unknown error'}`
        );
        setRegenerationLoading(false);
      }
    } catch (err) {
      setError('Failed to regenerate summary');
      console.error('Error regenerating summary:', err);
      setRegenerationLoading(false);
    }
  }, [pollRegenerationStatus]);

  const pollCyclicStatus = useCallback(async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.CYCLIC_STATUS)
        );
        const data = await response.json();

        if (data.success) {
          setCyclicStatus(data.status);

          // If cyclic scraping is complete, stop polling
          if (!data.status.running) {
            clearInterval(pollInterval);
            setCyclicLoading(false);
            if (data.status.error) {
              setError(`Cyclic scraping failed: ${data.status.error}`);
            }
          }
        }
      } catch (err) {
        console.error('Error polling cyclic status:', err);
        clearInterval(pollInterval);
        setCyclicLoading(false);
      }
    }, 2000); // Poll every 2 seconds

    // Cleanup interval after 24 hours to prevent infinite polling
    setTimeout(
      () => {
        clearInterval(pollInterval);
        setCyclicLoading(false);
      },
      24 * 60 * 60 * 1000
    );
  }, []);

  // Cyclic scraping functions
  const startCyclicScraping = useCallback(async () => {
    setCyclicLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.CYCLIC_START),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: selectedYear })
        }
      );
      const data = await response.json();

      if (data.success) {
        // Start polling for cyclic status
        pollCyclicStatus();
      } else {
        setError(
          `Failed to start cyclic scraping: ${data.error || 'Unknown error'}`
        );
        setCyclicLoading(false);
      }
    } catch (err) {
      setError('Failed to start cyclic scraping');
      console.error('Error starting cyclic scraping:', err);
      setCyclicLoading(false);
    }
  }, [selectedYear, pollCyclicStatus]);

  // Cyclic tail scraping
  const startCyclicTailScraping = useCallback(async () => {
    setCyclicLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getScraperUrl('/scraping/cyclic/start-tail'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: selectedYear })
        }
      );
      const data = await response.json();

      if (data.success) {
        // Start polling for cyclic status
        pollCyclicStatus();
      } else {
        setError(
          `Failed to start cyclic tail scraping: ${data.error || 'Unknown error'}`
        );
        setCyclicLoading(false);
      }
    } catch (err) {
      setError('Failed to start cyclic tail scraping');
      console.error('Error starting cyclic tail scraping:', err);
      setCyclicLoading(false);
    }
  }, [selectedYear, pollCyclicStatus]);

  // Cyclic full scraping
  const startCyclicFullScraping = useCallback(async () => {
    setCyclicLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getScraperUrl('/scraping/cyclic/start-full'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: selectedYear })
        }
      );
      const data = await response.json();

      if (data.success) {
        // Start polling for cyclic status
        pollCyclicStatus();
      } else {
        setError(
          `Failed to start cyclic full scraping: ${data.error || 'Unknown error'}`
        );
        setCyclicLoading(false);
      }
    } catch (err) {
      setError('Failed to start cyclic full scraping');
      console.error('Error starting cyclic full scraping:', err);
      setCyclicLoading(false);
    }
  }, [selectedYear, pollCyclicStatus]);

  // Cyclic proceedings scraping
  const startCyclicProceedingsScraping = useCallback(async () => {
    setCyclicLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getScraperUrl('/scraping/cyclic/start-proceedings'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: selectedYear })
        }
      );
      const data = await response.json();

      if (data.success) {
        // Start polling for cyclic status
        pollCyclicStatus();
      } else {
        setError(
          `Failed to start cyclic proceedings scraping: ${data.error || 'Unknown error'}`
        );
        setCyclicLoading(false);
      }
    } catch (err) {
      setError('Failed to start cyclic proceedings scraping');
      console.error('Error starting cyclic proceedings scraping:', err);
      setCyclicLoading(false);
    }
  }, [selectedYear, pollCyclicStatus]);

  const stopCyclicScraping = useCallback(async () => {
    try {
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.CYCLIC_STOP),
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setCyclicStatus((prev) => (prev ? { ...prev, running: false } : null));
        setCyclicLoading(false);
      } else {
        setError(
          `Failed to stop cyclic scraping: ${data.error || 'Unknown error'}`
        );
      }
    } catch (err) {
      setError('Failed to stop cyclic scraping');
      console.error('Error stopping cyclic scraping:', err);
    }
  }, []);

  // Poll cyclic status on component mount if running
  useEffect(() => {
    const checkInitialStatus = async () => {
      try {
        const response = await fetch(
          getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.CYCLIC_STATUS)
        );
        const data = await response.json();
        if (data.success && data.status.running) {
          setCyclicStatus(data.status);
          pollCyclicStatus();
        }
      } catch (err) {
        console.error('Error checking initial cyclic status:', err);
      }
    };
    checkInitialStatus();
  }, [pollCyclicStatus]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Redis Scraper Control
              </h1>
              <p className="text-gray-600 mt-2">
                Manage Redis-based scraping jobs and monitor progress
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={refreshAll}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh All
              </button>
            </div>
          </div>
        </div>

        {/* Year Selector */}
        <div className="mb-6 p-4 bg-white rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-600" />
              <label
                htmlFor="year-select"
                className="text-sm font-medium text-gray-700"
              >
                Year:
              </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={cyclicStatus?.running || cyclicLoading}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="23">2023</option>
                <option value="24">2024</option>
                <option value="25">2025</option>
                <option value="26">2026</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              {cyclicStatus?.running ? (
                <span className="text-blue-600">
                  Currently running for year 20{cyclicStatus.current_year}
                </span>
              ) : (
                <span>Select year for operations</span>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Two-Level Control Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Level 1: Overall Operations */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <Database className="w-6 h-6 text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Overall Operations
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              System-wide operations that affect all data
            </p>

            <div className="space-y-3">
              <button
                onClick={regenerateSummary}
                disabled={regenerationLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Database className="w-4 h-4 mr-2" />
                {regenerationLoading ? 'Regenerating...' : 'Regenerate Summary'}
              </button>

              <button
                onClick={() => triggerMerge(true)}
                disabled={mergeLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Merge className="w-4 h-4 mr-2" />
                {mergeLoading ? 'Merging...' : 'Merge Patches (with backup)'}
              </button>

              <button
                onClick={() => triggerMerge(false)}
                disabled={mergeLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                title="⚠️ Warning: This will merge without creating a backup. Use with caution!"
              >
                <Merge className="w-4 h-4 mr-2" />
                {mergeLoading ? 'Merging...' : 'Merge Patches (no backup)'}
              </button>

              <button
                onClick={triggerRegenerateProcessedData}
                disabled={regenerateProcessedLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                title="Regenerate all processed data files and consolidate user edits"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${regenerateProcessedLoading ? 'animate-spin' : ''}`}
                />
                {regenerateProcessedLoading
                  ? 'Regenerating...'
                  : 'Regenerate Processed Data'}
              </button>
            </div>

            {/* Regeneration Status */}
            {regenerationStatus && regenerationStatus.running && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    Regenerating...
                  </span>
                  <span className="text-sm text-blue-600">
                    {regenerationStatus.progress}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${regenerationStatus.progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Merge Status */}
            {mergeStatus && (
              <div
                className={`mt-4 p-3 rounded-lg ${
                  mergeStatus.running
                    ? 'bg-blue-50'
                    : mergeStatus.success
                      ? 'bg-green-50'
                      : 'bg-red-50'
                }`}
              >
                <div className="flex items-center">
                  <Merge className="w-4 h-4 mr-2" />
                  <span className="text-sm">{mergeStatus.message}</span>
                </div>
              </div>
            )}

            {/* Regenerate Processed Data Status */}
            {regenerateProcessedStatus && (
              <div
                className={`mt-4 p-3 rounded-lg ${
                  regenerateProcessedStatus.running
                    ? 'bg-blue-50'
                    : regenerateProcessedStatus.success
                      ? 'bg-green-50'
                      : 'bg-red-50'
                }`}
              >
                <div className="flex items-center">
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${regenerateProcessedStatus.running ? 'animate-spin' : ''}`}
                  />
                  <span className="text-sm">
                    {regenerateProcessedStatus.message}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Level 2: Year-Level Operations */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <Calendar className="w-6 h-6 text-green-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Year Operations
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Operations for year 20{selectedYear} across all prefixes
            </p>

            <div className="space-y-3">
              <button
                onClick={startCyclicScraping}
                disabled={cyclicLoading || cyclicStatus?.running}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Play className="w-4 h-4 mr-2" />
                {cyclicLoading ? 'Starting...' : 'Start Cyclic (Default)'}
              </button>

              <button
                onClick={startCyclicTailScraping}
                disabled={cyclicLoading || cyclicStatus?.running}
                className="w-full flex items-center justify-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                {cyclicLoading ? 'Starting...' : 'Start Cyclic (Tail)'}
              </button>

              <button
                onClick={startCyclicFullScraping}
                disabled={cyclicLoading || cyclicStatus?.running}
                className="w-full flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <ArrowDown className="w-4 h-4 mr-2" />
                {cyclicLoading ? 'Starting...' : 'Start Cyclic (Full)'}
              </button>

              <button
                onClick={startCyclicProceedingsScraping}
                disabled={cyclicLoading || cyclicStatus?.running}
                className="w-full flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {cyclicLoading ? 'Starting...' : 'Start Cyclic (Proceedings)'}
              </button>

              {cyclicStatus?.running && (
                <button
                  onClick={stopCyclicScraping}
                  className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Stop Cyclic Scraping
                </button>
              )}
            </div>

            {/* Cyclic Status */}
            {cyclicStatus && (
              <div
                className={`mt-4 p-3 rounded-lg ${
                  cyclicStatus.running
                    ? 'bg-blue-50'
                    : cyclicStatus.error
                      ? 'bg-red-50'
                      : 'bg-green-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Play className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">
                      {cyclicStatus.running
                        ? 'Cyclic Running'
                        : 'Cyclic Complete'}
                    </span>
                  </div>
                  <span className="text-sm">
                    {cyclicStatus.completed_prefixes?.length || 0} /{' '}
                    {cyclicStatus.total_prefixes || 0}
                  </span>
                </div>
                {cyclicStatus.running && (
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${((cyclicStatus.completed_prefixes?.length || 0) / (cyclicStatus.total_prefixes || 1)) * 100}%`
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <RawDataAnalysis
              loading={
                loading ||
                regenerationLoading ||
                mergeLoading ||
                regenerateProcessedLoading ||
                cyclicLoading
              }
              onRefresh={refreshAll}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScraperManagementDashboard;
